import React, { useState } from "react";
import ProfileAvatar from "components/common/ProfileAvatar";
import PbRecordGrid from "components/profile/PbRecordGrid";
import bucketIcon from "assets/images/icons/buccl_logo_B.png";
import "assets/scss/components/profileCard.scss";

const BrandStrip = ({ brand, onInstructorClick }) => {
    const clickable = typeof onInstructorClick === "function";
    return (
        <div
            className={`profile-card__brand-strip${clickable ? " profile-card__brand-strip--clickable" : ""}`}
            onClick={clickable ? onInstructorClick : undefined}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={(e) => {
                if (!clickable) return;
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onInstructorClick();
                }
            }}
        >
            <span className="profile-card__brand-badge">강사</span>
            {brand && <span className="profile-card__brand-name">{brand}</span>}
            {clickable && (
                <span className="profile-card__brand-cta">
                    강사 프로필 보기
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path
                            d="M6 4l4 4-4 4"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
            )}
        </div>
    );
};

const CertRow = ({ certs, max = 3 }) => {
    const [expanded, setExpanded] = useState(false);
    if (!certs || certs.length === 0) return null;
    const sorted = [...certs].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
    const shown = expanded ? sorted : sorted.slice(0, max);
    const extra = sorted.length - max;
    return (
        <div
            className={`profile-card__cert-row${
                expanded ? " profile-card__cert-row--expanded" : ""
            }`}
        >
            <span className="profile-card__cert-label">🎓 자격증</span>
            {shown.map((c) => (
                <span key={c.id} className="profile-card__cert-chip">
                    {c.name}
                </span>
            ))}
            {!expanded && extra > 0 && (
                <button
                    type="button"
                    className="profile-card__cert-chip profile-card__cert-chip--more"
                    onClick={() => setExpanded(true)}
                >
                    +{extra}
                </button>
            )}
            {expanded && sorted.length > max && (
                <button
                    type="button"
                    className="profile-card__cert-chip profile-card__cert-chip--more"
                    onClick={() => setExpanded(false)}
                >
                    접기
                </button>
            )}
        </div>
    );
};

// 공개 버킷리스트 노출 행 (커뮤니티 프로필 / 마이버클) — BE #449 §3 노출 규칙 적용분만 전달됨
const BucketRow = ({ items, max = 3 }) => {
    if (!items || items.length === 0) return null;
    const shown = items.slice(0, max);
    return (
        <div className="profile-card__bucket-row">
            <span className="profile-card__bucket-label">
                <img
                    className="profile-card__bucket-icon"
                    src={bucketIcon}
                    alt=""
                />
                버킷리스트
            </span>
            {shown.map((b) => (
                <span key={b.id} className="profile-card__bucket-chip">
                    {b.is_achieved && (
                        <span
                            className="profile-card__bucket-chip-check"
                            aria-label="달성"
                        >
                            ✓
                        </span>
                    )}
                    {b.content}
                </span>
            ))}
        </div>
    );
};

/**
 * 공통 프로필 카드 (시안 B v2 적용).
 * @param {object} profile - getProfileV2 응답 (nickname/name/profile_image_url/bio/level/preferred_sport_name/is_staff/brand_name/certificates/best_records)
 * @param {"mybuccle"|"community"} variant
 * @param {number} [postCount] - community variant 에서 이름 옆 인라인 노출
 * @param {function} [onEdit] - mybuccle variant 우상단 편집 링크 콜백 (없으면 미노출)
 * @param {ReactNode} [trailingAction] - 카드 body 우상단에 노출할 요소 (예: 차단/신고 메뉴)
 * @param {Array} [bucketList] - 공개 버킷리스트(최대 3개). getProfileV2 응답의 bucket_list
 */
const ProfileCard = ({
    profile,
    variant = "community",
    postCount,
    onEdit,
    onInstructorClick,
    onAvatarClick,
    trailingAction,
    bucketList,
}) => {
    if (!profile) return null;

    const isMybuccle = variant === "mybuccle";
    const displayName = profile.nickname || profile.name || "사용자";
    const hasBio = !!profile.bio;
    // 커뮤니티 프로필: 닉네임 아래 보조 라인으로 실명을 노출 (닉네임과 다를 때만)
    const realName =
        !isMybuccle && profile.name && profile.name !== displayName
            ? profile.name
            : null;

    return (
        <div className={`profile-card profile-card--${variant}`}>
            {!isMybuccle && profile.is_staff && (
                <BrandStrip
                    brand={profile.brand_name}
                    onInstructorClick={onInstructorClick}
                />
            )}

            <div className="profile-card__body">
                {trailingAction && (
                    <div className="profile-card__trailing">{trailingAction}</div>
                )}
                <div className="profile-card__main">
                    <ProfileAvatar
                        imageUrl={profile.profile_image_url}
                        size={isMybuccle ? 64 : 56}
                        className="profile-card__avatar"
                        onClick={
                            profile.profile_image_url && onAvatarClick
                                ? onAvatarClick
                                : undefined
                        }
                    />
                    <div className="profile-card__info">
                        <div className="profile-card__name-row">
                            <span className="profile-card__name">{displayName}</span>
                            {!isMybuccle && postCount != null && (
                                <span className="profile-card__post-count">
                                    · 게시글 <b className="tnum">{postCount}</b>
                                </span>
                            )}
                            {isMybuccle && onEdit && (
                                <button
                                    type="button"
                                    className="profile-card__edit-btn"
                                    onClick={onEdit}
                                >
                                    편집
                                </button>
                            )}
                        </div>

                        {realName && (
                            <p className="profile-card__real-name">{realName}</p>
                        )}

                        {hasBio && <p className="profile-card__bio">{profile.bio}</p>}
                    </div>
                </div>

                <CertRow certs={profile.certificates} />
                <BucketRow items={bucketList} />
                <PbRecordGrid records={profile.best_records} />
            </div>
        </div>
    );
};

export default ProfileCard;
